import { useEffect, useMemo, useState } from 'react';
import { shallowEqual } from 'react-redux';
import {
  Accordion,
  Banner,
  Box,
  Button,
  CloseButton,
  Heading,
  Icon,
  Image,
  Layout,
  List,
  Page,
  Paragraph,
  Table,
  Text,
  TextButton,
  Tooltip,
} from '@zillow/constellation-10';
import {
  IconCheckmarkFilled,
  IconChevronLeftFilled,
  IconConcealFilled,
  IconDollarSignCircleFilled,
  IconExternalFilled,
  IconHandshakeFilled,
  IconTourDoorFilled,
  IconUserGroupFilled,
  IconYardSignFilled,
} from '@zillow/constellation-icons';

import TenantPlacementMultiPartnerButton from './TenantPlacementMultiPartnerButton';
import { useHistory, useLocation } from 'react-router-dom';
import { SpinnerContainer } from '@zg-rentals/rental-manager-env';

import { useSelector } from '../../selectors/useSelector';
import { AppSelectors } from '../../selectors';

import { withErrorBoundary } from '../../components/ErrorBoundary';
import HeadWrapper from '../HeadWrapper';
import TenantPlacementCSEvents from '../../model/ClickstreamEvent/TenantPlacementCSEvents';
import type { PageLocation } from '../../utils/tenantPlacementUtils';
import DoorsteadLogo from '../../images/tenantPlacement/doorstead-logo.svg';

import { useGetListingDetails } from '../../hooks/queries/properties';
import accessUtils from '../../utils/accessUtils';

import {
  determinePartner,
  getPartnerTenantPlacementUrl,
  getPartnerTenantPlacementUrlByListingAddressAndLotId,
  getPartnerTenantPlacementUrlByPropertyDetailsData,
  TENANT_PLACEMENT_PARTNERS,
} from '../../utils/tenantPlacementUtils';

import BannerBackgroundImage from '../../images/tenantPlacement/tenant-placement-hero-image-desktop.svg';
import BannerBackgroundImageTablet from '../../images/tenantPlacement/tenant-placement-hero-image-tablet.svg';
import BannerBackgroundImageMweb from '../../images/tenantPlacement/tenant-placement-hero-image-mobile.svg';

import { bannerFlexStyles, bannerImageStyles, introBannerTextContainerStyles } from './styles';

export const TENANT_PLACEMENT_ENTRY_POINTS = {
  PRICE_MY_RENTAL: 'price_my_rental',
  NEW_LISTING: 'new_listing',
  PREVIOUS_LISTING: 'previous_listing',
  DASHBOARD_BANNER: 'dashboard_banner',
  DASHBOARD_LISTING_BLOCK: 'listblock_dashboard_banner',
  PROPERTIES_BANNER: 'properties_banner',
  ZRM_HOMEPAGE: 'zrm_homepage',
};

const isFromValidEntryPoint = (from: string | null) => from === TENANT_PLACEMENT_ENTRY_POINTS.PRICE_MY_RENTAL;

const TenantPlacement = () => {
  const location = useLocation();
  const history = useHistory();

  const { user, appConfig, isScreenMobile, isScreenTablet } = useSelector(
    (state) => ({
      user: state.user,
      appConfig: state.app.appConfig,
      isScreenMobile: AppSelectors.makeIsScreenMobile(state),
      isScreenTablet: AppSelectors.makeIsScreenTablet(state),
    }),
    shallowEqual,
  );

  const isNomadPartnerEnabled: boolean = accessUtils.isNomadPartnerEnabled({ appConfig });

  useEffect(() => {
    TenantPlacementCSEvents.TenantPlacementViewPage({ user });
  }, []);

  const queryParams = new URLSearchParams(location.search ? location.search : location.hash?.substring(1));
  const from = queryParams.get('from');
  const variation = queryParams.get('var');
  const utmTerm = queryParams.get('utm_term');
  const listingAlias = queryParams.get('listingAlias');
  const listingAddress = queryParams.get('listingAddress');
  const lotId = queryParams.get('lotId');
  const stateCode = queryParams.get('state') || '';
  const hasBackButton = isFromValidEntryPoint(from);

  const [selectedState, setSelectedState] = useState(stateCode);
  const {
    data: propertyDetailsData,
    isLoading: isListingDetailsLoading,
    error: listingDetailsError,
  } = useGetListingDetails(listingAlias);

  const canAccessTenantPlacementCopy = variation?.toLowerCase().split(',').includes('pm') ? true : false;

  const getBackgroundImage = () => {
    if (isScreenMobile) {
      return BannerBackgroundImageMweb;
    }
    if (isScreenTablet) {
      return BannerBackgroundImageTablet;
    }
    return BannerBackgroundImage;
  };

  const partnerUrlWithParams = useMemo(() => {
    const utmCampaign = canAccessTenantPlacementCopy ? 'zrm_lp_202502_pm' : 'zrm_lp_202409';
    const partner = isNomadPartnerEnabled ? determinePartner(selectedState) : TENANT_PLACEMENT_PARTNERS.DOORSTEAD;
    if (!listingAlias && listingAddress && lotId) {
      return getPartnerTenantPlacementUrlByListingAddressAndLotId(
        partner,
        listingAddress,
        lotId,
        user,
        from,
        utmCampaign,
        utmTerm,
      );
    }

    if (listingAlias && propertyDetailsData) {
      return getPartnerTenantPlacementUrlByPropertyDetailsData(
        partner,
        // @ts-expect-error
        propertyDetailsData,
        user,
        from,
        utmCampaign,
        utmTerm,
      );
    }

    return getPartnerTenantPlacementUrl(partner, user, from, utmCampaign, utmTerm);
  }, [user, propertyDetailsData, canAccessTenantPlacementCopy, selectedState]);

  const onGoBack = () => {
    history.goBack();
  };

  const getOnDoorsteadCtaClickHandler = (pageLocation: PageLocation) => () => {
    TenantPlacementCSEvents.TenantPlacementGoToDoorstead({
      user,
      pageLocation,
    });
  };

  if (isListingDetailsLoading && listingAlias && user?.loggedIn) {
    return (
      <Page.Root data-testid="TenantPlacementPage">
        <HeadWrapper />
        <SpinnerContainer />
      </Page.Root>
    );
  }

  if (listingDetailsError) {
    return (
      <Page.Root data-testid="TenantPlacementPage">
        <HeadWrapper />
        <Banner
          tone="critical"
          closeButton={<CloseButton onImpact />}
          onClose={() => window.location.reload()}
          data-testid="TenantPlacementPage-ErrorBanner"
        >
          Oops, there was an issue loading the page. Please try again and refresh the page
        </Banner>
      </Page.Root>
    );
  }

  return (
    <Page.Root
      data-testid="TenantPlacementPage"
      data-eventid={TenantPlacementCSEvents.TenantPlacementViewPage.data_event_id}
      data-eventdescription="View tenant placement page"
    >
      <HeadWrapper />
      <Box css={{ position: 'relative', backgroundColor: 'white' }}>
        <Box css={bannerImageStyles} style={{ backgroundImage: `url(${getBackgroundImage()})` }} />
        <Box css={{ display: 'flex', justifyContent: 'center' }}>
          <Box css={introBannerTextContainerStyles}>
            {hasBackButton && (
              <Page.Breadcrumb css={{ paddingLeft: { base: '400', xl: '0' } }}>
                <TextButton
                  onClick={onGoBack}
                  data-testid="TenantPlacementPage-BackBtn"
                  onImpact
                  icon={<IconChevronLeftFilled />}
                >
                  Back
                </TextButton>
              </Page.Breadcrumb>
            )}
            <Box
              css={{
                ...bannerFlexStyles,
                marginTop: { base: '600', md: hasBackButton ? '64px' : '80px', xxl: hasBackButton ? '600' : '64px' },
              }}
            >
              <Paragraph css={{ textStyle: 'heading-md', color: 'white' }}>Fill your vacancy fast</Paragraph>
              <Box css={{ marginTop: '200', marginBottom: { base: '400', md: '800' } }}>
                <Layout.Root>
                  <Layout.Content
                    css={{
                      gridColumnStart: '1',
                      gridColumnEnd: { base: '12', xxl: '10' },
                      padding: '0',
                      backgroundColor: 'transparent',
                    }}
                  >
                    <Paragraph css={{ textStyle: { base: 'body', xxl: 'heading-xs' }, color: 'white' }}>
                      Fill your rental vacancy with a qualified tenant using professional tenant placement{' '}
                      {canAccessTenantPlacementCopy ? 'or property management ' : ''}services from
                      {isNomadPartnerEnabled ? ` Zillow's partner.` : ` Doorstead, Zillow's exclusive partner.`}
                    </Paragraph>
                  </Layout.Content>
                </Layout.Root>
              </Box>
              {isNomadPartnerEnabled ? (
                <TenantPlacementMultiPartnerButton
                  stateCode={stateCode}
                  pageLocation="top_hero"
                  partnerUrlWithParams={partnerUrlWithParams}
                  onSetSelectedState={setSelectedState}
                  selectedState={selectedState}
                />
              ) : (
                <Box css={{ marginTop: { base: '200', lg: '400' } }}>
                  <Button
                    tone="brand"
                    emphasis="filled"
                    onClick={getOnDoorsteadCtaClickHandler('top_hero')}
                    data-eventid={TenantPlacementCSEvents.TenantPlacementGoToDoorstead.data_event_id}
                    data-testid="TenantPlacementPage-GetStartedBtn-TopHero"
                    css={{ paddingBlock: '400', paddingInline: { base: '400', xxl: '600' } }}
                    icon={<Icon size="md" css={{ marginLeft: '100' }} render={<IconExternalFilled />} />}
                    iconPosition="end"
                    asChild
                  >
                    <a href={partnerUrlWithParams} target="_blank" rel="nofollow noreferrer">
                      Learn More
                    </a>
                  </Button>
                </Box>
              )}
              <Box css={{ marginTop: { base: '400', md: '800' } }}>
                <Paragraph css={{ color: 'white', textStyle: { base: 'body-sm', xxl: 'body' } }}>
                  {isNomadPartnerEnabled
                    ? 'Available in select markets.'
                    : 'Available for select locations in CA, FL, TX, and WA.'}
                </Paragraph>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box
        id="tenant-placement-page-main-content"
        css={{ backgroundColor: 'white', _before: { content: '" "', display: 'table' } }}
      >
        <div>
          <Page.Content css={{ maxWidth: '892px', marginTop: '54px' }}>
            <Box css={{ marginBottom: '800' }} data-testid="TenantPlacementPage-WhatsIncludedSection">
              <Paragraph css={{ textStyle: 'heading-md', marginBottom: '200' }}>
                What you get with full-service tenant placement
              </Paragraph>
              <Box css={{ marginTop: '600' }}>
                <Layout.Root css={{ gap: { base: '0', lg: '800' } }}>
                  <Layout.Content
                    css={{ gridColumnEnd: { base: 'span 12', lg: 'span 6' }, paddingInline: '0', paddingBlock: '200' }}
                  >
                    <Icon render={<IconUserGroupFilled />} size="xl" />
                    <Paragraph css={{ textStyle: { base: 'body-lg-bold', smDown: 'body' }, marginBlock: '200' }}>
                      Tenant qualification
                    </Paragraph>

                    <Paragraph textStyle="body-sm" color="text.subtle">
                      Each tenant&apos;s identity, income, credit, background, employment history, and rental history,
                      are checked or verified to meet your criteria and protect against fraud.{' '}
                    </Paragraph>
                  </Layout.Content>
                  <Layout.Content
                    css={{ gridColumnEnd: { base: 'span 12', lg: 'span 6' }, paddingInline: '0', paddingBlock: '200' }}
                  >
                    <Icon render={<IconConcealFilled />} size="xl" />
                    <Paragraph css={{ textStyle: { base: 'body-lg-bold', smDown: 'body' }, marginBlock: '200' }}>
                      World-class fraud detection
                    </Paragraph>

                    <Paragraph textStyle="body-sm" color="text.subtle">
                      Top-tier verification systems are used to review every tenant application and protect you from
                      fraud
                    </Paragraph>
                  </Layout.Content>
                  <Layout.Content
                    css={{ gridColumnEnd: { base: 'span 12', lg: 'span 6' }, paddingInline: '0', paddingBlock: '200' }}
                  >
                    <Icon render={<IconTourDoorFilled />} size="xl" />
                    <Paragraph css={{ textStyle: { base: 'body-lg-bold', smDown: 'body' }, marginBlock: '200' }}>
                      Agent-led tours
                    </Paragraph>

                    <Paragraph textStyle="body-sm" color="text.subtle">
                      Pre-qualified leads can book tours with licensed real estate agents, often with same-day
                      availability.
                    </Paragraph>
                  </Layout.Content>
                  <Layout.Content
                    css={{ gridColumnEnd: { base: 'span 12', lg: 'span 6' }, paddingInline: '0', paddingBlock: '200' }}
                  >
                    <Icon render={<IconYardSignFilled />} size="xl" />
                    <Paragraph css={{ textStyle: { base: 'body-lg-bold', smDown: 'body' }, marginBlock: '200' }}>
                      Complete listing packages
                    </Paragraph>
                    <Paragraph textStyle="body-sm" color="text.subtle">
                      Let your listing shine with Zillow Premium&apos;s additional exposure, info on leads in your area,
                      rent pricing guidance, and optional professional listing photography.
                    </Paragraph>
                  </Layout.Content>
                  <Layout.Content
                    css={{ gridColumnEnd: { base: 'span 12', lg: 'span 6' }, paddingInline: '0', paddingBlock: '200' }}
                  >
                    <Icon render={<IconHandshakeFilled />} size="xl" />
                    <Paragraph css={{ textStyle: { base: 'body-lg-bold', smDown: 'body' }, marginBlock: '200' }}>
                      Full transparency & control
                    </Paragraph>

                    <Paragraph textStyle="body-sm" color="text.subtle">
                      Stay hands-off while getting updates at every step. You maintain full control over which tenant to
                      select.
                    </Paragraph>
                  </Layout.Content>
                  <Layout.Content
                    css={{ gridColumnEnd: { base: 'span 12', lg: 'span 6' }, paddingInline: '0', paddingBlock: '200' }}
                  >
                    <Icon render={<IconDollarSignCircleFilled />} size="xl" />
                    <Paragraph css={{ textStyle: { base: 'body-lg-bold', smDown: 'body' }, marginBlock: '200' }}>
                      No obligation or commitment
                    </Paragraph>

                    <Paragraph textStyle="body-sm" color="text.subtle">
                      You only pay 50% of the first month&apos;s rent once a tenant signs the lease—no upfront financial
                      commitment required.{' '}
                    </Paragraph>
                  </Layout.Content>
                </Layout.Root>
              </Box>
            </Box>
            <Box css={{ marginBottom: '800' }} data-testid="TenantPlacementPage-ComparisonTableSection">
              <Paragraph css={{ textStyle: 'heading-md', marginBottom: '1400' }}>
                {canAccessTenantPlacementCopy
                  ? 'What you get with Property Management'
                  : 'How it stacks up against the competition'}
              </Paragraph>
              <Box css={{ marginTop: { base: '600', lg: '1200' } }}>
                {canAccessTenantPlacementCopy ? (
                  <Table.Root
                    appearance="horizontal"
                    aria-label="Medium-sized horizontal table"
                    size="md"
                    alignments={['left', 'center', 'center']}
                    css={{ marginBottom: '1200' }}
                  >
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell css={{ width: '33.33%', verticalAlign: 'middle' }}>
                          <Text css={{ textStyle: { base: 'body-xs-bold', lg: 'body-lg-bold' } }}>
                            What&apos;s included
                          </Text>
                        </Table.HeaderCell>
                        <Table.HeaderCell css={{ width: '33.33%', verticalAlign: 'middle' }}>
                          <Text css={{ textStyle: { base: 'body-xs-bold', lg: 'body-lg-bold' } }}>
                            Tenant Placement
                          </Text>
                        </Table.HeaderCell>
                        <Table.HeaderCell css={{ width: '33.33%', verticalAlign: 'middle' }}>
                          <Text css={{ textStyle: { base: 'body-xs-bold', lg: 'body-lg-bold' } }}>
                            Property Management
                          </Text>
                        </Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      <Table.Row>
                        <Table.HeaderCell css={{ verticalAlign: 'middle' }}>
                          <Text css={{ textStyle: { base: 'body-sm', lg: 'body' } }}>
                            Ongoing rental pricing guidance
                          </Text>
                        </Table.HeaderCell>
                        <Table.Cell css={{ verticalAlign: 'middle' }}>
                          <Icon size="md" css={{ color: 'icon.accent.brand.hero' }} render={<IconCheckmarkFilled />} />
                        </Table.Cell>
                        <Table.Cell css={{ verticalAlign: 'middle' }}>
                          <Icon size="md" css={{ color: 'icon.accent.brand.hero' }} render={<IconCheckmarkFilled />} />
                        </Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.HeaderCell css={{ verticalAlign: 'middle' }}>
                          <Box css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Paragraph css={{ textStyle: { base: 'body-sm', lg: 'body' } }}>
                              Zillow Premium listings
                            </Paragraph>
                            <Tooltip content="On average, Zillow Premium listings get 1.5x the page views as typical Zillow listings." />
                          </Box>
                        </Table.HeaderCell>
                        <Table.Cell css={{ verticalAlign: 'middle' }}>
                          <Icon size="md" css={{ color: 'icon.accent.brand.hero' }} render={<IconCheckmarkFilled />} />
                        </Table.Cell>
                        <Table.Cell css={{ verticalAlign: 'middle' }}>
                          <Icon size="md" css={{ color: 'icon.accent.brand.hero' }} render={<IconCheckmarkFilled />} />
                        </Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.HeaderCell css={{ verticalAlign: 'middle' }}>
                          <Box css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Paragraph css={{ textStyle: { base: 'body-sm', lg: 'body' } }}>
                              Professional photography
                              <Paragraph css={{ textStyle: { base: 'fineprint', lg: 'body-xs' } }}>
                                Optional. No additional charge.
                              </Paragraph>
                            </Paragraph>

                            <Tooltip content="Book a professional photographer to capture your home at its best. Photos can typically be taken within 24 hours of request." />
                          </Box>
                        </Table.HeaderCell>
                        <Table.Cell css={{ verticalAlign: 'middle' }}>
                          <Icon size="md" css={{ color: 'icon.accent.brand.hero' }} render={<IconCheckmarkFilled />} />
                          <Paragraph textStyle="body-xs">Fast availability</Paragraph>
                        </Table.Cell>
                        <Table.Cell css={{ verticalAlign: 'middle' }}>
                          <Icon size="md" css={{ color: 'icon.accent.brand.hero' }} render={<IconCheckmarkFilled />} />
                          <Paragraph textStyle="body-xs">Fast availability</Paragraph>
                        </Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.HeaderCell css={{ verticalAlign: 'middle' }}>
                          <Box css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Paragraph css={{ textStyle: { base: 'body-sm', lg: 'body' } }}>
                              Real estate agent-led tours{' '}
                            </Paragraph>
                            <Tooltip content="Prospective tenants can book tours, typically with same- and next-day availability, with licensed real estate agents." />
                          </Box>
                        </Table.HeaderCell>
                        <Table.Cell css={{ verticalAlign: 'middle' }}>
                          <Icon size="md" css={{ color: 'icon.accent.brand.hero' }} render={<IconCheckmarkFilled />} />
                          <Paragraph textStyle="body-xs">Fast availability</Paragraph>
                        </Table.Cell>
                        <Table.Cell css={{ verticalAlign: 'middle' }}>
                          <Icon size="md" css={{ color: 'icon.accent.brand.hero' }} render={<IconCheckmarkFilled />} />
                          <Paragraph textStyle="body-xs">Fast availability</Paragraph>
                        </Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.HeaderCell css={{ verticalAlign: 'middle' }}>
                          <Box css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Paragraph css={{ textStyle: { base: 'body-sm', lg: 'body' } }}>
                              3-factor identity verification
                            </Paragraph>
                            <Tooltip content="Three-factor authentication is used to verify the tenant's identity. This includes tenant live photo, ID photo, and government database check." />
                          </Box>
                        </Table.HeaderCell>
                        <Table.Cell css={{ verticalAlign: 'middle' }}>
                          <Icon size="md" css={{ color: 'icon.accent.brand.hero' }} render={<IconCheckmarkFilled />} />
                        </Table.Cell>
                        <Table.Cell css={{ verticalAlign: 'middle' }}>
                          <Icon size="md" css={{ color: 'icon.accent.brand.hero' }} render={<IconCheckmarkFilled />} />
                        </Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.HeaderCell css={{ verticalAlign: 'middle' }}>
                          <Text css={{ textStyle: { base: 'body-sm', lg: 'body' } }}>Tenant screening</Text>
                        </Table.HeaderCell>
                        <Table.Cell css={{ verticalAlign: 'middle' }}>
                          <Icon size="md" css={{ color: 'icon.accent.brand.hero' }} render={<IconCheckmarkFilled />} />
                        </Table.Cell>
                        <Table.Cell css={{ verticalAlign: 'middle' }}>
                          <Icon size="md" css={{ color: 'icon.accent.brand.hero' }} render={<IconCheckmarkFilled />} />
                        </Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.HeaderCell css={{ verticalAlign: 'middle' }}>
                          <Text css={{ textStyle: { base: 'body-sm', lg: 'body' } }}>Lease preparation</Text>
                        </Table.HeaderCell>
                        <Table.Cell css={{ verticalAlign: 'middle' }}>
                          <Icon size="md" css={{ color: 'icon.accent.brand.hero' }} render={<IconCheckmarkFilled />} />
                        </Table.Cell>
                        <Table.Cell css={{ verticalAlign: 'middle' }}>
                          <Icon size="md" css={{ color: 'icon.accent.brand.hero' }} render={<IconCheckmarkFilled />} />
                        </Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.HeaderCell css={{ verticalAlign: 'middle' }}>
                          <Text css={{ textStyle: { base: 'body-sm', lg: 'body' } }}>Maintenance 24/7</Text>
                        </Table.HeaderCell>
                        <Table.Cell css={{ verticalAlign: 'middle' }}>{null}</Table.Cell>
                        <Table.Cell css={{ verticalAlign: 'middle' }}>
                          <Icon size="md" css={{ color: 'icon.accent.brand.hero' }} render={<IconCheckmarkFilled />} />
                        </Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.HeaderCell css={{ verticalAlign: 'middle' }}>
                          <Text css={{ textStyle: { base: 'body-sm', lg: 'body' } }}>
                            Rent collection & distribution
                          </Text>
                        </Table.HeaderCell>
                        <Table.Cell css={{ verticalAlign: 'middle' }}>{null}</Table.Cell>
                        <Table.Cell css={{ verticalAlign: 'middle' }}>
                          <Icon size="md" css={{ color: 'icon.accent.brand.hero' }} render={<IconCheckmarkFilled />} />
                        </Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.HeaderCell css={{ verticalAlign: 'middle' }}>
                          <Text css={{ textStyle: { base: 'body-sm', lg: 'body' } }}>Security deposit holding</Text>
                        </Table.HeaderCell>
                        <Table.Cell css={{ verticalAlign: 'middle' }}>{null}</Table.Cell>
                        <Table.Cell css={{ verticalAlign: 'middle' }}>
                          <Icon size="md" css={{ color: 'icon.accent.brand.hero' }} render={<IconCheckmarkFilled />} />
                        </Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.HeaderCell css={{ verticalAlign: 'middle' }}>
                          <Text css={{ textStyle: { base: 'body-sm', lg: 'body' } }}>Move-in/out evaluations</Text>
                        </Table.HeaderCell>
                        <Table.Cell css={{ verticalAlign: 'middle' }}>{null}</Table.Cell>
                        <Table.Cell css={{ verticalAlign: 'middle' }}>
                          <Icon size="md" css={{ color: 'icon.accent.brand.hero' }} render={<IconCheckmarkFilled />} />
                        </Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.HeaderCell css={{ verticalAlign: 'middle' }}>
                          <Text css={{ textStyle: { base: 'body-sm', lg: 'body' } }}>Expense and income tracking</Text>
                        </Table.HeaderCell>
                        <Table.Cell css={{ verticalAlign: 'middle' }}>{null}</Table.Cell>
                        <Table.Cell css={{ verticalAlign: 'middle' }}>
                          <Icon size="md" css={{ color: 'icon.accent.brand.hero' }} render={<IconCheckmarkFilled />} />
                        </Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.HeaderCell css={{ verticalAlign: 'middle' }}>
                          <Text css={{ textStyle: { base: 'body-sm', lg: 'body' } }}>Lease renewal</Text>
                        </Table.HeaderCell>
                        <Table.Cell css={{ verticalAlign: 'middle' }}>{null}</Table.Cell>
                        <Table.Cell css={{ verticalAlign: 'middle' }}>
                          <Icon size="md" css={{ color: 'icon.accent.brand.hero' }} render={<IconCheckmarkFilled />} />
                        </Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.HeaderCell css={{ verticalAlign: 'middle' }}>
                          <Text css={{ textStyle: { base: 'body-sm', lg: 'body' } }}>Competitive pricing</Text>
                        </Table.HeaderCell>
                        <Table.Cell css={{ verticalAlign: 'middle' }}>
                          <Icon size="md" css={{ color: 'icon.accent.brand.hero' }} render={<IconCheckmarkFilled />} />
                          <Paragraph textStyle="body-xs">Tenant Placement: 50% first month&apos;s rent</Paragraph>
                        </Table.Cell>
                        <Table.Cell css={{ verticalAlign: 'middle' }}>
                          <Icon size="md" css={{ color: 'icon.accent.brand.hero' }} render={<IconCheckmarkFilled />} />
                          <Paragraph textStyle="body-xs">Tenant Placement: 50% first month&apos;s rent</Paragraph>
                          <Paragraph textStyle="body-xs">Management fee: 4-6% monthly rent</Paragraph>
                        </Table.Cell>
                      </Table.Row>
                    </Table.Body>
                  </Table.Root>
                ) : (
                  <Table.Root
                    appearance="horizontal"
                    aria-label="Medium-sized horizontal table"
                    size="md"
                    alignments={['left', 'center', 'center']}
                    css={{ marginBottom: '1200' }}
                  >
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell css={{ width: '33.33%', verticalAlign: 'middle' }}>
                          <Text css={{ textStyle: { base: 'body-xs-bold', lg: 'body-lg-bold' } }}>
                            What&apos;s included
                          </Text>
                        </Table.HeaderCell>
                        <Table.HeaderCell
                          css={{ width: '33.33%', backgroundColor: 'blue.100', verticalAlign: 'middle' }}
                        >
                          <Text css={{ textStyle: { base: 'body-xs-bold', lg: 'body-lg-bold' } }}>
                            {isNomadPartnerEnabled ? `Zillow's partner services` : `Zillow's services with Doorstead`}
                          </Text>
                        </Table.HeaderCell>
                        <Table.HeaderCell css={{ width: '33.33%', verticalAlign: 'middle' }}>
                          <Text css={{ textStyle: { base: 'body-xs-bold', lg: 'body-lg-bold' } }}>
                            Typical placement services*
                          </Text>
                        </Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      <Table.Row>
                        <Table.HeaderCell css={{ verticalAlign: 'middle' }}>
                          <Text css={{ textStyle: { base: 'body-sm', lg: 'body' } }}>
                            Ongoing rental pricing guidance
                          </Text>
                        </Table.HeaderCell>
                        <Table.Cell css={{ backgroundColor: 'blue.100', verticalAlign: 'middle' }}>
                          <Icon size="md" css={{ color: 'icon.accent.brand.hero' }} render={<IconCheckmarkFilled />} />
                        </Table.Cell>
                        <Table.Cell css={{ verticalAlign: 'middle' }}>{null}</Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.HeaderCell css={{ verticalAlign: 'middle' }}>
                          <Box css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Paragraph css={{ textStyle: { base: 'body-sm', lg: 'body' } }}>
                              Zillow Premium listings
                            </Paragraph>
                            <Tooltip content="On average, Zillow Premium listings get 1.5x the page views as typical Zillow listings." />
                          </Box>
                        </Table.HeaderCell>
                        <Table.Cell css={{ backgroundColor: 'blue.100', verticalAlign: 'middle' }}>
                          <Icon size="md" css={{ color: 'icon.accent.brand.hero' }} render={<IconCheckmarkFilled />} />
                        </Table.Cell>
                        <Table.Cell css={{ verticalAlign: 'middle' }}>{null}</Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.HeaderCell css={{ verticalAlign: 'middle' }}>
                          <Box css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Paragraph css={{ textStyle: { base: 'body-sm', lg: 'body' } }}>
                              Professional photography
                              <Paragraph css={{ textStyle: { base: 'fineprint', lg: 'body-xs' } }}>
                                Optional. No additional charge.
                              </Paragraph>
                            </Paragraph>

                            <Tooltip content="Book a professional photographer to capture your home at its best. Photos can typically be taken within 24 hours of request." />
                          </Box>
                        </Table.HeaderCell>
                        <Table.Cell css={{ backgroundColor: 'blue.100', verticalAlign: 'middle' }}>
                          <Icon size="md" css={{ color: 'icon.accent.brand.hero' }} render={<IconCheckmarkFilled />} />
                          <Paragraph textStyle="body-xs">Fast availability</Paragraph>
                        </Table.Cell>
                        <Table.Cell css={{ verticalAlign: 'middle' }}>
                          <Icon size="md" render={<IconCheckmarkFilled />} />
                        </Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.HeaderCell css={{ verticalAlign: 'middle' }}>
                          <Box css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Paragraph css={{ textStyle: { base: 'body-sm', lg: 'body' } }}>
                              Real estate agent-led tours{' '}
                            </Paragraph>
                            <Tooltip content="Prospective tenants can book tours, typically with same- and next-day availability, with licensed real estate agents." />
                          </Box>
                        </Table.HeaderCell>
                        <Table.Cell css={{ backgroundColor: 'blue.100', verticalAlign: 'middle' }}>
                          <Icon size="md" css={{ color: 'icon.accent.brand.hero' }} render={<IconCheckmarkFilled />} />
                          <Paragraph textStyle="body-xs">Fast availability</Paragraph>
                        </Table.Cell>
                        <Table.Cell css={{ verticalAlign: 'middle' }}>
                          <Icon size="md" render={<IconCheckmarkFilled />} />
                        </Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.HeaderCell css={{ verticalAlign: 'middle' }}>
                          <Box css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Paragraph css={{ textStyle: { base: 'body-sm', lg: 'body' } }}>
                              3-factor identity verification
                            </Paragraph>
                            <Tooltip content="Three-factor authentication is used to verify the tenant's identity. This includes tenant live photo, ID photo, and government database check." />
                          </Box>
                        </Table.HeaderCell>
                        <Table.Cell css={{ backgroundColor: 'blue.100', verticalAlign: 'middle' }}>
                          <Icon size="md" css={{ color: 'icon.accent.brand.hero' }} render={<IconCheckmarkFilled />} />
                        </Table.Cell>
                        <Table.Cell css={{ verticalAlign: 'middle' }}>{null}</Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.HeaderCell css={{ verticalAlign: 'middle' }}>
                          <Text css={{ textStyle: { base: 'body-sm', lg: 'body' } }}>Tenant screening</Text>
                        </Table.HeaderCell>
                        <Table.Cell css={{ backgroundColor: 'blue.100', verticalAlign: 'middle' }}>
                          <Icon size="md" css={{ color: 'icon.accent.brand.hero' }} render={<IconCheckmarkFilled />} />
                        </Table.Cell>
                        <Table.Cell css={{ verticalAlign: 'middle' }}>
                          <Icon size="md" render={<IconCheckmarkFilled />} />
                        </Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.HeaderCell css={{ verticalAlign: 'middle' }}>
                          <Text css={{ textStyle: { base: 'body-sm', lg: 'body' } }}>Lease preparation</Text>
                        </Table.HeaderCell>
                        <Table.Cell css={{ backgroundColor: 'blue.100', verticalAlign: 'middle' }}>
                          <Icon size="md" css={{ color: 'icon.accent.brand.hero' }} render={<IconCheckmarkFilled />} />
                        </Table.Cell>
                        <Table.Cell css={{ verticalAlign: 'middle' }}>
                          <Icon size="md" render={<IconCheckmarkFilled />} />
                        </Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.HeaderCell css={{ verticalAlign: 'middle' }}>
                          <Box css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Paragraph css={{ textStyle: { base: 'body-sm', lg: 'body' } }}>Owner dashboard</Paragraph>
                            <Tooltip content="The owner portal shows your progress every step of the way, and is where you can customize your preferences." />
                          </Box>
                        </Table.HeaderCell>
                        <Table.Cell css={{ backgroundColor: 'blue.100', verticalAlign: 'middle' }}>
                          <Icon size="md" css={{ color: 'icon.accent.brand.hero' }} render={<IconCheckmarkFilled />} />
                        </Table.Cell>
                        <Table.Cell css={{ verticalAlign: 'middle' }}>{null}</Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.HeaderCell css={{ verticalAlign: 'middle' }}>
                          <Text css={{ textStyle: { base: 'body-sm', lg: 'body' } }}>Competitive pricing</Text>
                        </Table.HeaderCell>
                        <Table.Cell css={{ backgroundColor: 'blue.100', verticalAlign: 'middle' }}>
                          <Icon size="md" css={{ color: 'icon.accent.brand.hero' }} render={<IconCheckmarkFilled />} />
                          <Paragraph textStyle="body-xs">50% first month&apos;s rent</Paragraph>
                        </Table.Cell>
                        <Table.Cell css={{ verticalAlign: 'middle' }}>
                          <Paragraph textStyle="body-xs">75-100% first month&apos;s rent</Paragraph>
                        </Table.Cell>
                      </Table.Row>
                    </Table.Body>
                  </Table.Root>
                )}
                {!canAccessTenantPlacementCopy && !isNomadPartnerEnabled && (
                  <Box css={{ marginBlock: '1600' }}>
                    <Paragraph textStyle="body-xs" color="text.subtle">
                      *Based on a Doorstead survey of 10 companies offering tenant placement services across CA, TX and
                      WA (August 2024).
                    </Paragraph>
                  </Box>
                )}
              </Box>
            </Box>
          </Page.Content>

          <Box css={{ backgroundColor: 'bg.softer', width: '100%', display: 'flex', justifyContent: 'center' }}>
            <Box
              css={{
                maxWidth: '892px',
                display: 'flex',
                flexDirection: 'column',
                paddingBlock: { base: '800', lg: '1600' },
                marginInline: { base: '400', xl: '0' },
              }}
            >
              {!isNomadPartnerEnabled && (
                <Box css={{ alignSelf: 'start' }}>
                  <Image src={DoorsteadLogo} alt="Doorstead" css={{ marginBottom: '800' }} />
                </Box>
              )}
              <Heading level={3} css={{ marginBottom: '800' }}>
                {isNomadPartnerEnabled ? 'Why use a Zillow partner?' : 'Why use Doorstead with Zillow?'}
              </Heading>
              <Layout.Root css={{ marginBottom: '800', gap: '600' }}>
                <Layout.Content
                  css={{
                    gridColumnEnd: { base: 'span 12', lg: 'span 4' },
                    padding: '0',
                    backgroundColor: 'transparent',
                  }}
                >
                  <Heading level={5} css={{ marginBottom: '200' }}>
                    Save time
                  </Heading>
                  <Paragraph>
                    {isNomadPartnerEnabled
                      ? `Our partners handle the screenings and even conduct showings and professional photography. All you
                    need to do is choose a pre-screened tenant or let the partner decide.`
                      : `Doorstead handles the screenings and can even conduct showings and professional photography. All you
                    need to do is choose a pre-screened tenant or let Doorstead make the choice.`}
                  </Paragraph>
                </Layout.Content>
                <Layout.Content
                  css={{
                    gridColumnEnd: { base: 'span 12', lg: 'span 4' },
                    padding: '0',
                    backgroundColor: 'transparent',
                  }}
                >
                  <Heading level={5} css={{ marginBottom: '200' }}>
                    Reduce risk
                  </Heading>
                  <Paragraph>
                    {isNomadPartnerEnabled
                      ? `Our partner's rigorous vetting process ensures you receive tenants who have been thoroughly
                    screened, and meet your qualifications.`
                      : `Doorstead's rigorous vetting process ensures you receive tenants who have been thoroughly`}
                  </Paragraph>
                </Layout.Content>
                <Layout.Content
                  css={{
                    gridColumnEnd: { base: 'span 12', lg: 'span 4' },
                    padding: '0',
                    backgroundColor: 'transparent',
                  }}
                >
                  <Heading level={5} css={{ marginBottom: '200' }}>
                    Exclusive Zillow Features
                  </Heading>
                  <Paragraph>
                    {isNomadPartnerEnabled
                      ? `Get added exposure with Zillow Premium listing included, and the benefit of unlimited agent-led
                    tours.`
                      : `With Doorstead's partnership with Zillow, you get added exposure with a Zillow Premium listing,
                    and the benefit of unlimited agent-led tours.`}
                  </Paragraph>
                </Layout.Content>
              </Layout.Root>
              <Box css={{ alignSelf: 'start' }}>
                {isNomadPartnerEnabled ? (
                  <TenantPlacementMultiPartnerButton
                    stateCode={stateCode}
                    pageLocation="bottom_of_page"
                    partnerUrlWithParams={partnerUrlWithParams}
                    onSetSelectedState={setSelectedState}
                    selectedState={selectedState}
                  />
                ) : (
                  <Button
                    tone="brand"
                    emphasis="outlined"
                    css={{ paddingBlock: '400', paddingInline: '800' }}
                    onClick={getOnDoorsteadCtaClickHandler('bottom_of_page')}
                    data-testid="TenantPlacementPage-GetStartedBtn-BottomOfPage"
                    icon={<Icon size="md" css={{ marginLeft: '100' }} render={<IconExternalFilled />} />}
                    iconPosition="end"
                    asChild
                  >
                    <a href={partnerUrlWithParams} target="_blank" rel="nofollow noreferrer">
                      Get started
                    </a>
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
          <Page.Content css={{ maxWidth: '892px', marginTop: '54px' }}>
            <Box css={{ paddingBlock: '400' }} data-testid="TenantPlacementPage-LearnMoreSection">
              <Heading level={3} css={{ marginBottom: '600', color: 'text.express.insight.strong' }}>
                Frequently asked questions
              </Heading>
              <Box css={{ marginBottom: '600' }}>
                <Accordion.Root>
                  <Accordion.Item value="tenant-placement-vs-zrm">
                    <Accordion.Header>
                      <Heading level={5} css={{ textStyle: 'body' }}>
                        What&apos;s the difference between tenant placement and using Zillow Rental Manager?
                      </Heading>
                    </Accordion.Header>
                    <Accordion.Panel>
                      <Paragraph css={{ marginBottom: '400' }}>
                        Tenant placement is a full-service option we are offering through{' '}
                        {isNomadPartnerEnabled ? `Zillow's partner ` : `Doorstead `}
                        to connect property owners to qualified tenants. It covers everything necessary to find a
                        tenant, including listing creation, professional photography, scheduling and coordinating with
                        prospective renters, tours, tenant screening, lease preparation and signing, and security
                        deposit collection.
                      </Paragraph>

                      <Paragraph css={{ marginBottom: '400' }}>
                        The cost is 50% of the first month&apos;s rent, and you only pay once a tenant successfully
                        signs a lease.
                      </Paragraph>

                      <Paragraph css={{ marginBottom: '400' }}>
                        This service is only available to landlords in{' '}
                        {isNomadPartnerEnabled
                          ? `some states and zip codes.`
                          : `certain parts of California, Florida, Texas, and Washington.`}
                      </Paragraph>

                      <Paragraph css={{ marginBottom: '400' }}>
                        Zillow Rental Manager offers free tools that help property owners find and manage tenants on
                        their own. This includes posting a listing, accepting rental applications, signing a lease, and
                        accepting rental payments.
                      </Paragraph>
                    </Accordion.Panel>
                  </Accordion.Item>
                  <Accordion.Item value="how-long-place-tenant">
                    <Accordion.Header>
                      <Heading level={5} css={{ textStyle: 'body' }}>
                        How long does it typically take to place a tenant?
                      </Heading>
                    </Accordion.Header>
                    <Accordion.Panel>
                      It takes 14 days on average to place a qualified tenant. You&apos;ll stay in control with full
                      visibility and decision-making power through your personalized dashboard.
                    </Accordion.Panel>
                  </Accordion.Item>
                  <Accordion.Item value="tenant-screening-included">
                    <Accordion.Header>
                      <Heading level={5} css={{ textStyle: 'body' }}>
                        What&apos;s included in tenant screening?
                      </Heading>
                    </Accordion.Header>
                    <Accordion.Panel>
                      {isNomadPartnerEnabled ? `Zillow's partner's` : `Doorstead's`} screening process includes:
                      <List.Root>
                        <List.Item>Comprehensive background checks</List.Item>
                        <List.Item>Credit score verification (650+ required)</List.Item>
                        <List.Item>Income verification (3x rent minimum)</List.Item>
                        <List.Item>Employment history review</List.Item>
                        <List.Item>Rental history analysis</List.Item>
                        <List.Item>Advanced fraud detection</List.Item>
                        <List.Item>3-factor identity verification</List.Item>
                      </List.Root>
                    </Accordion.Panel>
                  </Accordion.Item>
                  <Accordion.Item value="no-obligation-policy">
                    <Accordion.Header>
                      <Heading level={5} css={{ textStyle: 'body' }}>
                        What are the details of the no obligation or commitment policy?
                      </Heading>
                    </Accordion.Header>
                    <Accordion.Panel>
                      With {isNomadPartnerEnabled ? `Zillow's partner's` : `Doorstead's`} no obligation policy, you only
                      pay 50% of the first month&apos;s rent once you successfully sign a lease with a tenant. Subject
                      to their terms, the service can be cancelled at any time before a lease is signed with no fees or
                      penalties.
                    </Accordion.Panel>
                  </Accordion.Item>
                </Accordion.Root>
              </Box>
            </Box>
          </Page.Content>
        </div>
      </Box>
    </Page.Root>
  );
};

export { TenantPlacement };
export default withErrorBoundary(TenantPlacement);
